import { move } from "@dnd-kit/helpers";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import getSymbolFromCurrency from "currency-symbol-map";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import {
  getDivisor,
  getFrequencyString,
  getIntervalLabel,
} from "../../../../utils/stripe-helpers";
import PaymentLinkQr from "./PaymentLinkQr";

const SortablePlanRow = ({ plan, index, render }) =>
  render(plan, useSortable({ id: plan.id, index }));

const PlansTable = ({
  isPress = false,
  plans,
  getPaymentLink,
  getPaymentLinkUrl,
  updatePlanStatus,
  preferPlan,
  togglePrivate,
  reorderPlans,
  planType = "press",
  gymLocations = [],
}) => {
  const { t } = useTranslation("coaching-portal");

  // Only published plans reach the storefront, so only they are reorderable;
  // unpublished ones stay listed below them. Local state so a drop shows
  // immediately while the new order saves.
  const savedActivePlans = useMemo(
    () => plans?.filter((plan) => plan.active) ?? [],
    [plans]
  );
  const [activePlans, setActivePlans] = useState(savedActivePlans);
  useEffect(() => setActivePlans(savedActivePlans), [savedActivePlans]);
  const inactivePlans = useMemo(
    () => plans?.filter((plan) => !plan.active) ?? [],
    [plans]
  );
  const activePlansLength = activePlans.length;

  const handleDragEnd = (event) => {
    if (event.canceled) return;
    const reordered = move(activePlans, event);
    if (reordered.every((plan, index) => plan === activePlans[index])) return;
    setActivePlans(reordered);
    reorderPlans(
      reordered.map(({ id }) => id),
      planType
    );
  };

  const StatusToggle = ({ published, id }) => {
    const [value, setValue] = useState(published);
    const [openedConfirmationModal, setOpenedConfirmationModal] =
      useState(false);

    return (
      <>
        <ToolTip
          id={`plan-status${id}`}
          body={
            published
              ? t("planManager.plansTable.unpublishPlan")
              : t("planManager.plansTable.publishPlan")
          }
        >
          <input
            type="checkbox"
            id={`switch-${id}`}
            name={`switch-${id}`}
            role="switch"
            onChange={() => {
              if (published && activePlansLength <= 2) {
                alert(t("planManager.plansTable.needTwoActivePlans"));
                return;
              }
              setValue(!value);
              setOpenedConfirmationModal(!openedConfirmationModal);
            }}
            checked={!!value}
          />
        </ToolTip>
        <ConfirmationModal
          title={
            !value
              ? t("planManager.plansTable.unpublish")
              : t("planManager.plansTable.publish")
          }
          contentText={
            !value
              ? t("planManager.plansTable.unpublishConfirm")
              : t("planManager.plansTable.publishConfirm")
          }
          cancelButtonText={t("planManager.plansTable.cancel")}
          cancelButtonAction={() => {
            setOpenedConfirmationModal(false);
            setValue(!value);
          }}
          confirmButtonText={
            !value
              ? t("planManager.plansTable.unpublish")
              : t("planManager.plansTable.publish")
          }
          confirmButtonAction={() => updatePlanStatus(id, value)}
          isOpen={openedConfirmationModal}
          confirmSuccess={value}
        />
      </>
    );
  };

  const renderRow = (plan, { ref, handleRef, isDragging } = {}) => {
    const {
      id,
      active,
      activeCount,
      currency,
      features,
      metadata = {},
      recurring,
      unit_amount,
      nickname,
      type,
      facilikey = [],
    } = plan;
    const {
      access = "",
      preferred = "false",
      private: isPrivate = "false",
      trial_days,
      minimum_term,
    } = metadata;
    const isPreferred = preferred === "true";
    const symbol = getSymbolFromCurrency(currency);
    const divisor = getDivisor(currency);
    const price = unit_amount / divisor;
    const locations = gymLocations.filter((value) =>
      facilikey.includes(value.facilikey)
    );
    const isYearly = recurring?.interval === "year";
    return (
      <div
        className={`row table-row${isDragging ? " dragging" : ""}`}
        key={id}
        ref={ref}
      >
        {isPreferred && <div className="row-ribbon" />}
        <div className="cell name">
          {handleRef && activePlansLength > 1 && (
            <button
              ref={handleRef}
              type="button"
              className="drag-handle"
              title={t("planManager.plansTable.dragToReorder")}
            >
              <SvgIcon
                src="/img/new-icons/menu.svg"
                alt={t("planManager.plansTable.dragToReorder")}
              />
            </button>
          )}
          {planType === "push"
            ? nickname || getIntervalLabel(recurring, t)
            : nickname || "-"}
        </div>
        {planType === "press" && (
          <div className="cell hide-md">
            {features?.length ? (
              <ol>
                {features.map((feature, i) => (
                  <li key={`${i}${feature}`}>{feature}</li>
                ))}
              </ol>
            ) : (
              "-"
            )}
          </div>
        )}
        <div className="cell price">
          <p>
            {symbol}
            {price}{" "}
          </p>
          <span className="text-light">({currency.toUpperCase()})</span>
        </div>
        {isPress && (
          <div className="cell hide-sm">
            {locations.length ? (
              <ul>
                {locations.map(({ gymName, gymLocation, gymContact }) => (
                  <li key={`${gymName}-${gymLocation}-${gymContact}`}>
                    {gymName}{" "}
                    <span className="text-light">({gymLocation})</span>
                  </li>
                ))}
              </ul>
            ) : (
              "-"
            )}
          </div>
        )}
        <div className="cell hide-sm">
          <div>
            {type === "recurring"
              ? t("planManager.plansTable.yes")
              : t("planManager.plansTable.no")}
          </div>
          <div className="text-light">({getIntervalLabel(recurring, t)})</div>
        </div>
        {isPress ? (
          <div className="cell hide-sm">
            <div>
              {access
                ? `${access[0].toUpperCase()}${access.slice(1)} Access`
                : "-"}
            </div>
            <div className="text-light">
              ({activeCount}{" "}
              {activeCount === 1
                ? t("planManager.plansTable.user")
                : t("planManager.plansTable.usersCount")}
              )
            </div>
          </div>
        ) : (
          <div className="cell hide-sm">{activeCount}</div>
        )}
        {planType === "push" && (
          <div className="cell hide-sm">
            {!trial_days
              ? t("planManager.plansTable.no")
              : trial_days > 1
                ? `${trial_days} ${t("planManager.plansTable.days")}`
                : `${trial_days} ${t("planManager.plansTable.day")}`}
          </div>
        )}
        {planType === "push" && (
          <div className="cell hide-sm">
            {!minimum_term
              ? t("planManager.plansTable.no")
              : `${minimum_term} ${getFrequencyString(
                  recurring,
                  minimum_term
                )}`}
          </div>
        )}
        <div
          className={`cell center narrow${isPress ? " actions-press" : " actions-push"}`}
        >
          <a
            role="button"
            data-button-type="primary"
            href={`/actions/refresh?modal[type]=${
              planType === "push" ? "plan-trial" : "create-plan"
            }&type=${planType}&plan=${id}`}
            className="edit icon-button"
            sz="small"
          >
            {planType === "push"
              ? t("planManager.plansTable.freeTrialButton")
              : t("planManager.plansTable.edit")}
          </a>
          <div className="icon-button-container">
            <ToolTip
              id={`plan-details${id}`}
              body={t("planManager.plansTable.viewPlanDetails")}
            >
              <a
                role="button"
                href={`/actions/refresh?modal[type]=plan-details&plan=${id}&type=${planType}`}
                data-button-type="secondary"
                data-icon-only
              >
                <SvgIcon
                  src="/img/new-icons/eye.svg"
                  alt={t("planManager.plansTable.viewAlt")}
                />
              </a>
            </ToolTip>
            <ToolTip
              id={`payment-link${id}`}
              body={
                active
                  ? t("planManager.plansTable.getPaymentLink")
                  : t("planManager.plansTable.inactivePlan")
              }
            >
              <a
                href="#"
                role="button"
                data-button-type="secondary"
                data-icon-only
                onClick={(e) =>
                  getPaymentLink(
                    e,
                    id,
                    isPress ? "gymmembership.app" : "programs.app"
                  )
                }
                style={active ? {} : { pointerEvents: "none" }}
              >
                <SvgIcon
                  src="/img/new-icons/link-white.svg"
                  alt={t("planManager.plansTable.paymentLinkAlt")}
                />
              </a>
            </ToolTip>
            {active && (
              <PaymentLinkQr
                id={id}
                planName={nickname}
                url={getPaymentLinkUrl(
                  id,
                  isPress ? "gymmembership.app" : "programs.app"
                )}
              />
            )}
            {!isPress && !isYearly && (
              <ToolTip
                id={`minimum-term-${id}`}
                body={t("planManager.plansTable.minimumTermTooltip")}
              >
                <a
                  href={`/actions/refresh?modal[type]=plan-minimum-term&type=${planType}&plan=${id}`}
                  role="button"
                  data-button-type="secondary"
                  data-icon-only
                >
                  <SvgIcon src="/img/new-icons/minimum-term.svg" />
                </a>
              </ToolTip>
            )}

            {isPress && (
              <ToolTip
                id={`private-plan${id}`}
                body={isPrivate === "true" ? "Make Public" : "Make Private"}
              >
                <a
                  role="button"
                  data-icon-only
                  data-button-type={
                    isPrivate === "true" ? "primary" : "secondary"
                  }
                  href="#"
                  onClick={(e) => togglePrivate(e, id, isPrivate)}
                >
                  <SvgIcon src="/img/new-icons/eye-slash.svg" alt="Private" />
                </a>
              </ToolTip>
            )}
            {isPress && (
              <ToolTip
                id={`feature-plan${id}`}
                body={
                  isPreferred
                    ? t("planManager.plansTable.removePreferred")
                    : t("planManager.plansTable.markAsPreferred")
                }
              >
                <a
                  role="button"
                  data-icon-only
                  data-button-type="secondary"
                  href="#"
                  onClick={(e) => preferPlan(e, id, preferred, plans)}
                >
                  <SvgIcon
                    src={
                      isPreferred
                        ? "/img/new-icons/star-gradient.svg"
                        : "/img/new-icons/star.svg"
                    }
                    alt={t("planManager.plansTable.favoriteAlt")}
                  />
                </a>
              </ToolTip>
            )}
          </div>
        </div>
        <div className="cell center">
          <StatusToggle published={active} id={id} />
        </div>
      </div>
    );
  };

  return (
    <div className="plans-table">
      {plans?.length ? (
        <div className="s1 row table-head">
          <p className="s1 cell">{t("planManager.plansTable.name")}</p>
          {isPress && (
            <p className="s1 cell hide-md">
              {t("planManager.plansTable.included")}
            </p>
          )}
          <p className="s1 cell price">{t("planManager.plansTable.price")}</p>
          {isPress && (
            <p className="s1 cell hide-sm">
              {t("planManager.plansTable.keyAccess")}
            </p>
          )}
          <p className="s1 cell hide-sm">
            {t("planManager.plansTable.recurring")}
          </p>
          <p className="s1 cell hide-sm">
            {isPress
              ? t("planManager.plansTable.userAccess")
              : t("planManager.plansTable.users")}
          </p>
          {planType === "push" && (
            <p className="s1 cell hide-sm">
              {t("planManager.plansTable.freeTrial")}
            </p>
          )}
          {planType === "push" && (
            <p className="s1 cell hide-sm">
              {t("planManager.plansTable.minimumTerm")}
            </p>
          )}
          <p className="s1 cell center narrow">
            {t("planManager.plansTable.actions")}
          </p>
          <p className="s1 cell center">{t("planManager.plansTable.status")}</p>
        </div>
      ) : null}

      {plans?.length ? (
        <>
          <DragDropProvider onDragEnd={handleDragEnd}>
            {activePlans.map((plan, index) => (
              <SortablePlanRow
                key={plan.id}
                plan={plan}
                index={index}
                render={renderRow}
              />
            ))}
          </DragDropProvider>
          {inactivePlans.map((plan) => renderRow(plan))}
        </>
      ) : (
        <div className="no-data">
          {t("planManager.plansTable.noPlansFound")}
        </div>
      )}
    </div>
  );
};

export default PlansTable;
